import { useEffect, useMemo } from "react";
import { ReactNode } from "react";
import { useFormContext, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { QuoteRequestLineOverthicknessDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLineOverthickness.api";
import { getMaterialLabelSuffix } from "#modules/quoteRequestLines/view/getMaterialLabelSuffix";
import { RawMaterialFormData } from "#modules/quoteRequestLines/view/MaterialLayoutModal";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { Flex } from "#shared/view/components/Flex";
import { RecommendedDiameterInputController } from "#shared/view/components/Form/RecommendedDiameterInputController";
import { RecommendedNumberInputController } from "#shared/view/designSystem/inputs/RecommendedNumberInputController";
import {
  FieldConfig,
  FormatterMaterialParameter,
  getMaterialDimensionsFieldsConfiguration,
  getOverThicknessFromPartType,
} from "shared";

type Props = {
  quoteRequestLineId: string | undefined;
  setValue: UseFormSetValue<RawMaterialFormData>;
  overthickness: QuoteRequestLineOverthicknessDto;
};

export function RawMaterialDimensionsForm({ quoteRequestLineId, setValue, overthickness }: Props) {
  const { t } = useTranslation();

  const { control, getValues } = useFormContext<RawMaterialFormData>();

  const { quoteRequestLine } = useQuoteRequestLine({
    id: quoteRequestLineId,
  });

  const part = quoteRequestLine?.part;
  const partType = part?.type;

  const materialLabelSuffix = getMaterialLabelSuffix({
    type: partType,
  });

  const fieldsConfig = useMemo(
    () => getMaterialDimensionsFieldsConfiguration(partType ?? undefined),
    [partType],
  );

  const getPartValueWithOverthickness = (
    field: keyof FormatterMaterialParameter,
  ): number | undefined => {
    const baseValue = part?.[field];
    if (!baseValue || field === "tubeThickness") {
      return baseValue ?? undefined;
    }
    const partOverthickness = getOverThicknessFromPartType(partType, overthickness);
    if (field === "height") {
      return baseValue + partOverthickness.height;
    }
    if (field === "width") {
      return baseValue + partOverthickness.width;
    }

    return baseValue + partOverthickness.length;
  };

  useEffect(() => {
    const allDimensionFields: Array<keyof FormatterMaterialParameter> = [
      "height",
      "width",
      "length",
    ];
    const activeFieldsForType = new Set(
      fieldsConfig.flatMap((f) => [f.field, f.syncWith].filter(Boolean)),
    );

    allDimensionFields.forEach((field) => {
      if (!activeFieldsForType.has(field)) {
        setValue(field, 0);
      }
    });

    if (!activeFieldsForType.has("tubeThickness")) {
      setValue("tubeThickness", null);
    }
  }, [partType, fieldsConfig, setValue]);

  const renderField = (fieldConfig: FieldConfig): ReactNode => {
    const formFieldName = fieldConfig.field;
    const partValue = getPartValueWithOverthickness(fieldConfig.field);

    const getMessage = (): string => {
      if (!partValue) {
        return "";
      }

      return t(`material.messages.${fieldConfig.key}`, { value: partValue });
    };

    const handleFieldChange = () => {
      if (fieldConfig.syncWith) {
        const currentValue = getValues(formFieldName);
        setValue(fieldConfig.syncWith, currentValue);
      }
    };

    if (fieldConfig.key === "diameter") {
      return (
        <RecommendedDiameterInputController
          key={fieldConfig.key}
          style={{ flex: 1 }}
          name={formFieldName}
          label={`${t(`material.${fieldConfig.key}`)} ${materialLabelSuffix}`}
          onChange={handleFieldChange}
          computedValue={{
            value: partValue,
            message: getMessage(),
          }}
          widthKey="width"
          heightKey="height"
        />
      );
    }

    return (
      <RecommendedNumberInputController
        key={fieldConfig.key}
        style={{ flex: 1 }}
        name={formFieldName}
        label={`${t(`material.${fieldConfig.key}`)} ${materialLabelSuffix}`}
        control={control}
        onChange={handleFieldChange}
        computedValue={{
          value: partValue ?? null,
          message: getMessage(),
        }}
        unit="mm"
      />
    );
  };

  return (
    <Flex gap="xs" align="end">
      {fieldsConfig.map(renderField)}
    </Flex>
  );
}
